import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config';
import { fetchData } from '../../utils/api';
import { useAdminLanguage } from '../../AdminLanguageContext';
import { translateAdmin } from '../../utils/adminTranslations';
import styles from '../../styles/scss/system/PaymentPeriods.module.scss';

const MONTHS_RU = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const emptyPeriod = () => ({
    name: '',
    start_month: 1,
    start_day: 1,
    end_month: 1,
    end_day: 1,
    booking_active: false,
    rooms_active: true,
});

const dayOptions = (month) => {
    const daysInMonth = new Date(2000, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, index) => index + 1);
};

const getPaymentMode = (period) => {
    if (period.booking_active && period.rooms_active) return 'both';
    if (period.booking_active) return 'booking';
    if (period.rooms_active) return 'room';
    return 'none';
};

const PaymentPeriods = ({ setHeaderTitle, embedded = false }) => {
    const { language } = useAdminLanguage();
    const translate = (text) => translateAdmin(text, language);
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!embedded && setHeaderTitle) setHeaderTitle(translate('Периоды оплаты'));
    }, [embedded, language, setHeaderTitle]);

    const loadPeriods = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetchData(`${apiUrl}/api/sys/payment-periods`, 'GET', token);
            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Не удалось загрузить периоды оплаты');
            }
            setPeriods(response.request.data.periods);
        } catch (loadError) {
            setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPeriods();
    }, [loadPeriods]);

    const updatePeriod = (index, changes) => {
        setPeriods((current) => current.map((period, periodIndex) => (
            periodIndex === index ? { ...period, ...changes } : period
        )));
    };

    const updateDatePart = (index, datePrefix, part, value) => {
        const period = periods[index];
        const month = part === 'month' ? Number(value) : period[`${datePrefix}_month`];
        const dayKey = `${datePrefix}_day`;
        const currentDay = part === 'day' ? Number(value) : period[dayKey];
        updatePeriod(index, {
            [`${datePrefix}_${part}`]: Number(value),
            [dayKey]: Math.min(currentDay, dayOptions(month).length),
        });
    };

    const movePeriod = (index, offset) => {
        const targetIndex = index + offset;
        if (targetIndex < 0 || targetIndex >= periods.length) return;
        setPeriods((current) => {
            const next = [...current];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });
    };

    const savePeriods = async (nextPeriods) => {
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetchData(
                `${apiUrl}/api/sys/payment-periods`,
                'PUT',
                token,
                { body: { periods: nextPeriods } }
            );
            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Не удалось сохранить периоды оплаты');
            }
            setMessage(response.request.message);
            await loadPeriods();
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        savePeriods(periods.map((period) => ({
            ...period,
            start_month: Number(period.start_month),
            start_day: Number(period.start_day),
            end_month: Number(period.end_month),
            end_day: Number(period.end_day),
        })));
    };

    const renderDateSelects = (period, index, prefix, label) => (
        <div className={styles.dateField}>
            <span>{label}</span>
            <div className={styles.dateControls}>
                <select
                    aria-label={`${translate(label)}: ${translate('месяц')}`}
                    value={period[`${prefix}_month`]}
                    onChange={(event) => updateDatePart(index, prefix, 'month', event.target.value)}
                >
                    {MONTHS_RU.map((month, monthIndex) => (
                        <option key={month} value={monthIndex + 1}>{translate(month)}</option>
                    ))}
                </select>
                <select
                    aria-label={`${translate(label)}: ${translate('день')}`}
                    value={period[`${prefix}_day`]}
                    onChange={(event) => updateDatePart(index, prefix, 'day', event.target.value)}
                >
                    {dayOptions(period[`${prefix}_month`]).map((day) => (
                        <option key={day} value={day}>{day}</option>
                    ))}
                </select>
            </div>
        </div>
    );

    if (loading) return <div className={styles.status}>{translate('Загрузка периодов оплаты...')}</div>;

    return (
        <section className={styles.page}>
            <p className={styles.description}>
                {translate('Периоды повторяются каждый год. Если дата окончания раньше даты начала, период проходит через Новый год. Например, с 20 октября по 30 сентября.')}
            </p>
            {periods.length === 0 && (
                <p className={styles.notice} role="status">
                    {translate('Периоды оплаты не настроены. Онлайн-поиск и бронирование будут отклоняться для любых дат.')}
                </p>
            )}
            {error && <p className={styles.error} role="alert">{translate(error)}</p>}
            {message && <p className={styles.success} role="status">{translate(message)}</p>}

            <div className={styles.periodList}>
                {periods.map((period, index) => (
                    <article className={styles.periodCard} key={`${period.id || 'new'}-${index}`}>
                        <div className={styles.cardHeading}>
                            <h2>{translate('Период')} {index + 1}</h2>
                            <div className={styles.orderButtons}>
                                <button type="button" onClick={() => movePeriod(index, -1)} disabled={index === 0} aria-label={translate('Выше')}>↑</button>
                                <button type="button" onClick={() => movePeriod(index, 1)} disabled={index === periods.length - 1} aria-label={translate('Ниже')}>↓</button>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => setPeriods((current) => current.filter((_, i) => i !== index))}
                                >
                                    {translate('Удалить')}
                                </button>
                            </div>
                        </div>
                        <label className={styles.nameField}>
                            {translate('Название')}
                            <input
                                maxLength="100"
                                value={period.name || ''}
                                onChange={(event) => updatePeriod(index, { name: event.target.value })}
                                placeholder={translate('Например, высокий сезон')}
                            />
                        </label>
                        <div className={styles.datesRow}>
                            {renderDateSelects(period, index, 'start', translate('Начало'))}
                            {renderDateSelects(period, index, 'end', translate('Окончание'))}
                        </div>
                        <label className={styles.modeField}>
                            {translate('Что оплачивает клиент')}
                            <select
                                value={getPaymentMode(period)}
                                onChange={(event) => {
                                    const mode = event.target.value;
                                    updatePeriod(index, {
                                        booking_active: mode === 'booking' || mode === 'both',
                                        rooms_active: mode === 'room' || mode === 'both',
                                    });
                                }}
                            >
                                <option value="booking">{translate('Только бронирование')}</option>
                                <option value="room">{translate('Только номер')}</option>
                                <option value="both">{translate('Бронирование и номер')}</option>
                                <option value="none">{translate('Без оплаты')}</option>
                            </select>
                        </label>
                    </article>
                ))}
            </div>

            <div className={styles.actions}>
                <button type="button" onClick={() => setPeriods((current) => [...current, emptyPeriod()])}>
                    {translate('Добавить период')}
                </button>
                <button type="button" className={styles.saveButton} onClick={handleSave} disabled={saving}>
                    {saving ? translate('Сохранение...') : translate('Сохранить периоды')}
                </button>
            </div>
            <p className={styles.hint}>
                {translate('При пересечении периодов применяется период, который расположен выше в списке.')}
            </p>
        </section>
    );
};

export default PaymentPeriods;
